import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { leaderboardApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Trophy, Medal, Loader2, Radio } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Leaderboard = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [live, setLive] = useState(false);
  const [pulseKey, setPulseKey] = useState(0);
  const { user, isAuthenticated } = useAuth();
  const wsRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        const data = await leaderboardApi.getAll();
        if (!cancelled) setLeaderboard(data);
      } catch (err) {
        console.error('Error fetching leaderboard:', err);
        if (!cancelled) setError('Failed to load leaderboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchLeaderboard();

    // Open WebSocket for live updates. Non-blocking — UI still works if the
    // socket fails to open (e.g. behind a proxy that strips Upgrade headers).
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    // https://... → wss://..., http://... → ws://...
    const wsUrl = backend.replace(/^http/, 'ws') + '/api/leaderboard/ws';
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
    } catch (e) {
      console.debug('leaderboard ws ctor failed', e);
      return () => { cancelled = true; };
    }

    ws.onopen = () => { if (!cancelled) setLive(true); };
    ws.onclose = () => { if (!cancelled) setLive(false); };
    ws.onerror = () => { if (!cancelled) setLive(false); };
    ws.onmessage = (ev) => {
      if (cancelled) return;
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === 'leaderboard_update' && Array.isArray(msg.entries)) {
          setLeaderboard(msg.entries);
          setPulseKey((k) => k + 1); // trigger flash highlight
        }
      } catch (e) {
        console.debug('leaderboard ws parse failed', e);
      }
    };

    return () => {
      cancelled = true;
      try { ws && ws.close(); } catch (e) { /* noop */ }
    };
  }, []);

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-[#FFD700]" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-[#C0C0C0]" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-[#CD7F32]" />;
    return null;
  };

  const getRankColor = (rank) => {
    if (rank === 1) return 'border-[#FFD700]/50 bg-gradient-to-r from-[#FFD700]/10 to-transparent';
    if (rank === 2) return 'border-[#C0C0C0]/50 bg-gradient-to-r from-[#C0C0C0]/10 to-transparent';
    if (rank === 3) return 'border-[#CD7F32]/50 bg-gradient-to-r from-[#CD7F32]/10 to-transparent';
    return 'border-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00FF00]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-16">
        <div className="mb-8 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-4xl font-bold mb-3">Global Leaderboard</h1>
            <p className="text-gray-400 text-lg">Top cybersecurity learners worldwide</p>
          </div>
          <div
            data-testid="leaderboard-live-indicator"
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-mono ${
              live
                ? 'border-[#00FF00]/40 bg-[#00FF00]/10 text-[#00FF00]'
                : 'border-gray-700 bg-gray-900 text-gray-500'
            }`}
          >
            <Radio className={`w-3 h-3 ${live ? 'animate-pulse' : ''}`} />
            {live ? 'LIVE' : 'OFFLINE'}
          </div>
        </div>

        {error && (
          <div className="text-center py-12">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Top 3 Podium */}
        {leaderboard.length >= 3 && (
          <div className="grid md:grid-cols-3 gap-4 mb-8" data-testid="leaderboard-podium" key={`podium-${pulseKey}`}>
            {leaderboard.slice(0, 3).map((entry) => (
              <Card 
                key={entry.rank}
                className={`${getRankColor(entry.rank)} border transition-all duration-300 hover:transform hover:scale-105`}
              >
                <CardHeader className="text-center pb-3">
                  <div className="flex justify-center mb-3">
                    {getRankIcon(entry.rank)}
                  </div>
                  <CardTitle className="text-2xl text-white">#{entry.rank}</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="mb-3">
                    <div className="text-xl font-bold text-white mb-1">{entry.username}</div>
                    <Badge variant="outline" className="border-gray-700 text-gray-300">
                      {entry.country || 'Global'}
                    </Badge>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Points</span>
                      <span className="text-[#00FF00] font-semibold">{(entry.points || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Streak</span>
                      <span className="text-[#FF9500] font-semibold">{entry.streak || 0} days</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Rest of Leaderboard */}
        <Card className="bg-[#1A1A1A] border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">All Rankings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="leaderboard-list" key={`list-${pulseKey}`}>
              {leaderboard.map((entry, index) => (
                <div 
                  key={entry.rank}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    index < 3 ? getRankColor(entry.rank) : 'border-gray-800 hover:border-gray-700'
                  } transition-colors`}
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-12 text-center">
                      {index < 3 ? (
                        getRankIcon(entry.rank)
                      ) : (
                        <span className="text-gray-400 font-semibold">#{entry.rank}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-white">{entry.username}</div>
                      <div className="text-sm text-gray-400">{entry.country || 'Global'}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <div className="text-sm text-gray-400">Points</div>
                      <div className="font-semibold text-[#00FF00]">{(entry.points || 0).toLocaleString()}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-400">Streak</div>
                      <div className="font-semibold text-[#FF9500]">{entry.streak || 0} days</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Your Rank */}
        {isAuthenticated && user && (
          <Card className="bg-gradient-to-r from-[#00FF00]/10 to-[#00BFFF]/10 border-[#00FF00]/30 mt-6">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-400 mb-1">Your Rank</div>
                  <div className="text-3xl font-bold text-white">
                    {user.rank ? `#${user.rank}` : 'Unranked'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-400 mb-1">Your Points</div>
                  <div className="text-3xl font-bold text-[#00FF00]">{user.points || 0}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {leaderboard.length === 0 && !error && (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">No rankings available yet</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Leaderboard;
